<template>
  <section class="option-manager" :aria-label="item.name + '管理'">
    <div class="section-heading">
      <div>
        <h3>{{ item.name }}</h3>
        <p>{{ item.description }}</p>
      </div>
    </div>

    <div class="filter-bar">
      <label class="filter-field">
        <span>{{ item.name }}名称</span>
        <a-input v-model.trim="keyword" :placeholder="'请输入' + item.name + '名称'" allow-clear @pressEnter="search" />
      </label>
      <label class="filter-field date-field">
        <span>创建时间</span>
        <a-date-picker v-model="createdDate" placeholder="选择日期" style="width: 100%" />
      </label>
      <div class="filter-actions">
        <a-button type="primary" @click="search">搜索</a-button>
        <a-button @click="resetFilters">重置</a-button>
      </div>
    </div>

    <div class="table-toolbar">
      <a-button type="primary" @click="openAdd" v-hasPermi="['system:dict:add']">
        <a-icon type="plus" />新增{{ item.name }}
      </a-button>
      <span class="table-hint">停用后，新建档案不再显示该选项；已有档案不受影响。</span>
    </div>

    <a-table
      class="option-table"
      row-key="id"
      :columns="columns"
      :data-source="rows"
      :loading="loading"
      :pagination="false"
      :scroll="{ x: 780 }"
      bordered
      size="middle">
      <template slot="sequence" slot-scope="text, record, index">
        {{ (pageNum - 1) * pageSize + index + 1 }}
      </template>
      <template slot="remark" slot-scope="text">
        <span class="remark-text">{{ text || '—' }}</span>
      </template>
      <template slot="status" slot-scope="text, record">
        <a-switch
          :checked="record.status === 1"
          :loading="statusUpdatingId === record.id"
          :aria-label="record.dictLabel + (record.status === 1 ? '，已启用' : '，已停用')"
          @change="checked => changeStatus(record, checked)"
          v-hasPermi="['system:dict:edit']" />
        <span class="status-caption">{{ record.status === 1 ? '启用' : '停用' }}</span>
      </template>
      <template slot="creator" slot-scope="text, record">
        {{ creatorName(record.creator) }}
      </template>
      <template slot="createTime" slot-scope="text">
        {{ formatTime(text) }}
      </template>
      <template slot="operation" slot-scope="text, record">
        <a-button size="small" @click="openEdit(record)" v-hasPermi="['system:dict:edit']">编辑</a-button>
      </template>
    </a-table>

    <div v-if="total > 0" class="pagination-row">
      <a-pagination
        :current="pageNum"
        :page-size="pageSize"
        :page-size-options="['10', '20', '50']"
        :total="total"
        :show-total="showTotal"
        show-size-changer
        @change="changePage"
        @showSizeChange="changePageSize" />
    </div>

    <a-modal
      :title="(editing ? '编辑' : '新增') + item.name"
      :visible="editorVisible"
      :confirm-loading="saving"
      ok-text="保存"
      cancel-text="取消"
      :width="480"
      :destroy-on-close="true"
      class="erp-picker-modal basic-option-modal"
      @ok="submitForm"
      @cancel="closeEditor">
      <a-form-model ref="optionForm" :model="form" :rules="rules" layout="vertical">
        <a-form-model-item :label="item.name + '名称'" prop="dictLabel">
          <a-input v-model.trim="form.dictLabel" :max-length="100" :placeholder="'请输入' + item.name + '名称'" />
        </a-form-model-item>
        <a-form-model-item label="备注" prop="remark">
          <a-textarea v-model="form.remark" :rows="3" :max-length="500" placeholder="可填写说明（选填）" />
        </a-form-model-item>
        <a-form-model-item label="状态" prop="status">
          <a-radio-group v-model="form.status">
            <a-radio :value="1">启用</a-radio>
            <a-radio :value="0">停用</a-radio>
          </a-radio-group>
        </a-form-model-item>
      </a-form-model>
      <p v-if="editing" class="editor-note">修改名称不会改变已有档案中保存的选项；不再使用时请将状态设为“停用”。</p>
    </a-modal>
  </section>
</template>

<script>
import { pageData, addData, updateData } from '@/api/system/dict/data'
import { listUser } from '@/api/system/user'

export default {
  name: 'BasicOptionTable',
  props: {
    item: { type: Object, required: true }
  },
  data () {
    return {
      keyword: '',
      createdDate: null,
      pageNum: 1,
      pageSize: 10,
      rows: [],
      total: 0,
      loading: false,
      creatorNames: {},
      editorVisible: false,
      editing: false,
      saving: false,
      statusUpdatingId: null,
      form: { id: undefined, dictLabel: '', dictValue: '', status: 1, remark: '' },
      rules: {
        dictLabel: [{ required: true, whitespace: true, message: '请输入名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    columns () {
      return [
        { title: '序号', key: 'sequence', width: 64, scopedSlots: { customRender: 'sequence' } },
        { title: this.item.name + '名称', dataIndex: 'dictLabel', width: 180, ellipsis: true },
        { title: '备注', dataIndex: 'remark', scopedSlots: { customRender: 'remark' } },
        { title: '状态', dataIndex: 'status', width: 120, scopedSlots: { customRender: 'status' } },
        { title: '创建人', key: 'creator', width: 110, scopedSlots: { customRender: 'creator' } },
        { title: '创建时间', dataIndex: 'createTime', width: 156, scopedSlots: { customRender: 'createTime' } },
        { title: '操作', key: 'operation', width: 88, scopedSlots: { customRender: 'operation' } }
      ]
    }
  },
  created () {
    this.loadRows()
    this.loadCreators()
  },
  methods: {
    async loadRows () {
      this.loading = true
      const query = {
        dictType: this.item.type,
        dictLabel: this.keyword || undefined,
        pageNum: this.pageNum,
        pageSize: this.pageSize
      }
      if (this.createdDate) {
        const date = this.createdDate.format('YYYY-MM-DD')
        query.createBeginTime = date + ' 00:00:00'
        query.createEndTime = date + ' 23:59:59'
      }
      try {
        const response = await pageData(query)
        this.rows = (response.data && response.data.records) || []
        this.total = (response.data && response.data.total) || 0
      } finally {
        this.loading = false
      }
    },
    async loadCreators () {
      try {
        const response = await listUser({})
        const users = response.data || []
        this.creatorNames = users.reduce((names, user) => {
          names[String(user.id)] = user.nickName || user.userName
          return names
        }, {})
      } catch (error) {
        // Option maintenance still works if this account cannot query user profiles.
      }
    },
    creatorName (creator) {
      if (creator === null || creator === undefined || String(creator) === '0') return '系统初始化'
      return this.creatorNames[String(creator)] || '未知用户'
    },
    formatTime (value) {
      return value ? String(value).replace('T', ' ').slice(0, 16) : '—'
    },
    showTotal (total) { return '共 ' + total + ' 条' },
    search () {
      this.pageNum = 1
      this.loadRows()
    },
    resetFilters () {
      this.keyword = ''
      this.createdDate = null
      this.search()
    },
    changePage (page) {
      this.pageNum = page
      this.loadRows()
    },
    changePageSize (current, size) {
      this.pageSize = size
      this.pageNum = 1
      this.loadRows()
    },
    openAdd () {
      this.editing = false
      this.form = { id: undefined, dictLabel: '', dictValue: '', status: 1, remark: '' }
      this.editorVisible = true
    },
    openEdit (record) {
      this.editing = true
      this.form = {
        id: record.id,
        dictLabel: record.dictLabel,
        dictValue: record.dictValue,
        status: record.status,
        remark: record.remark || ''
      }
      this.editorVisible = true
    },
    closeEditor () { this.editorVisible = false },
    submitForm () {
      this.$refs.optionForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        const payload = {
          ...this.form,
          dictLabel: this.form.dictLabel.trim(),
          dictType: this.item.type
        }
        if (!this.editing) {
          // Preserve a stable internal value without asking users to learn dictionary codes.
          payload.dictValue = payload.dictLabel
          payload.dictSort = 0
        }
        try {
          if (this.editing) await updateData(payload)
          else await addData(payload)
          this.$message.success(this.editing ? '保存成功' : '新增成功')
          await this.$store.dispatch('dict/removeDict', this.item.type)
          this.notifyOptionChanged()
          this.closeEditor()
          this.loadRows()
        } finally {
          this.saving = false
        }
      })
    },
    changeStatus (record, checked) {
      const save = async () => {
        this.statusUpdatingId = record.id
        try {
          await updateData({
            id: record.id,
            dictLabel: record.dictLabel,
            dictValue: record.dictValue,
            dictType: this.item.type,
            status: checked ? 1 : 0,
            remark: record.remark
          })
          await this.$store.dispatch('dict/removeDict', this.item.type)
          this.notifyOptionChanged()
          this.$message.success(checked ? '已启用' : '已停用')
          this.loadRows()
        } finally {
          this.statusUpdatingId = null
        }
      }
      if (checked) return save()
      this.$confirm({
        title: '确认停用“' + record.dictLabel + '”？',
        content: '停用后，新建和编辑档案时将不再提供该选项，已有档案不受影响。',
        okText: '确认停用',
        cancelText: '取消',
        onOk: save
      })
    },
    notifyOptionChanged () {
      window.dispatchEvent(new CustomEvent('erp-basic-option-changed', {
        detail: { dictType: this.item.type }
      }))
    }
  }
}
</script>

<style scoped>
.section-heading { margin-bottom: 24px; }
.section-heading h3 { margin: 0 0 5px; color: #182434; font-size: 20px; font-weight: 600; }
.section-heading p { margin: 0; color: #718095; }
.filter-bar { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 12px 16px; margin-bottom: 20px; }
.filter-field { display: flex; flex-direction: column; gap: 7px; width: 210px; color: #344054; font-weight: 500; }
.date-field { width: 174px; }
.filter-actions { display: flex; gap: 8px; }
.table-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
.table-hint { color: #8591a2; font-size: 12px; text-align: right; }
.option-table /deep/ .ant-table-thead > tr > th { background: #f5f7fa; color: #445166; font-weight: 600; }
.option-table /deep/ .ant-table-tbody > tr:nth-child(even) > td { background: #fafbfd; }
.option-table /deep/ .ant-table-tbody > tr > td { color: #344054; }
.remark-text { color: #66758a; }
.status-caption { margin-left: 8px; color: #66758a; }
.pagination-row { display: flex; justify-content: flex-end; margin-top: 24px; }
.editor-note { margin: 4px 0 0; color: #718095; font-size: 12px; line-height: 1.6; }
@media (max-width: 1100px) { .table-hint { display: none; } }
@media (max-width: 640px) { .filter-field, .date-field { width: 100%; } .filter-actions { width: 100%; } .table-toolbar { align-items: flex-start; } }
</style>
